import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ImageUpload } from "@/components/ImageUpload";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Loader2, Inbox, Eye } from "lucide-react";
import { toast } from "sonner";

function defaultValue(f) {
  if (f.type === "switch") return false;
  if (f.type === "number") return 0;
  if (f.type === "image" || f.type === "file") return null;
  return "";
}

export function ResourceManager({ title, eyebrow, endpoint, fields, hasPublish = false, idField = null, readOnly = false, icon: Icon = Inbox, rowActions = null }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [viewItem, setViewItem] = useState(null);

  const buildEmpty = () => {
    const e = {};
    fields.forEach((f) => (e[f.name] = defaultValue(f)));
    if (hasPublish) e.published = false;
    return e;
  };

  const load = () => { setLoading(true); api.get(`/admin/${endpoint}`).then((r) => setItems(r.data)).finally(() => setLoading(false)); };
  useEffect(load, [endpoint]);

  const openAdd = () => { setEditing(null); setForm(buildEmpty()); setDialogOpen(true); };
  const openEdit = (item) => {
    setEditing(item);
    const f = {};
    fields.forEach((fl) => (f[fl.name] = item[fl.name] ?? defaultValue(fl)));
    if (hasPublish) f.published = !!item.published;
    setForm(f);
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/${endpoint}/${editing.id}`, form);
      else await api.post(`/admin/${endpoint}`, form);
      toast.success(editing ? "Updated" : "Created");
      setDialogOpen(false);
      load();
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  const togglePublish = async (item) => {
    try { await api.patch(`/admin/${endpoint}/${item.id}/publish`, { published: !item.published }); toast.success(!item.published ? "Published" : "Unpublished"); load(); }
    catch { toast.error("Failed to update"); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/admin/${endpoint}/${deleteId}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
    setDeleteId(null);
  };

  const tableFields = fields.filter((f) => f.table !== false && f.type !== "textarea");

  const renderCell = (item, f) => {
    const v = item[f.name];
    if (f.type === "image") return v ? <img src={fileUrl(v)} alt="" className="h-10 w-10 rounded object-cover" /> : <span className="text-[#CBD2CC]">—</span>;
    if (f.type === "file") return v ? <a href={fileUrl(v)} target="_blank" rel="noreferrer" className="text-[#C59B27] underline text-sm">View</a> : <span className="text-[#CBD2CC]">—</span>;
    if (f.type === "switch") return <Badge className={`border-0 ${v ? "bg-[#194324] text-white" : "bg-[#F2EEE5] text-[#5C7264]"}`}>{v ? "Yes" : "No"}</Badge>;
    return <span className="text-sm">{String(v ?? "").slice(0, 60) || "—"}</span>;
  };

  return (
    <div data-testid={`admin-${endpoint}-page`}>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B38728] font-mono">{eyebrow}</p>
          <h1 className="font-display text-3xl font-semibold text-[#194324]">{title}</h1>
        </div>
        {!readOnly && (
          <Button onClick={openAdd} className="bg-[#194324] hover:bg-[#2D623A] text-white rounded-full" data-testid={`admin-add-${endpoint}-button`}>
            <Plus className="h-4 w-4 mr-2" /> Add
          </Button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-[#E2DAD0] overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#C59B27]" /></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-[#5C7264]"><Icon className="h-10 w-10 mx-auto mb-3 text-[#CBD2CC]" />No entries yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F2EEE5]">
                  {idField && <TableHead>ID</TableHead>}
                  {tableFields.map((f) => <TableHead key={f.name}>{f.label}</TableHead>)}
                  {hasPublish && <TableHead>Published</TableHead>}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} data-testid={`${endpoint}-row-${item.id}`}>
                    {idField && <TableCell className="font-mono text-xs text-[#B38728]">{item[idField]}</TableCell>}
                    {tableFields.map((f) => <TableCell key={f.name}>{renderCell(item, f)}</TableCell>)}
                    {hasPublish && (
                      <TableCell>
                        <Switch checked={!!item.published} onCheckedChange={() => togglePublish(item)} data-testid={`${endpoint}-publish-toggle-${item.id}`} />
                      </TableCell>
                    )}
                    <TableCell className="text-right whitespace-nowrap">
                      {rowActions && rowActions(item)}
                      {!readOnly && <Button size="icon" variant="ghost" onClick={() => openEdit(item)} data-testid={`${endpoint}-edit-${item.id}`}><Pencil className="h-4 w-4 text-[#2D623A]" /></Button>}
                      {readOnly && <Button size="icon" variant="ghost" onClick={() => setViewItem(item)} data-testid={`${endpoint}-view-${item.id}`}><Eye className="h-4 w-4 text-[#2D623A]" /></Button>}
                      <Button size="icon" variant="ghost" onClick={() => setDeleteId(item.id)} data-testid={`${endpoint}-delete-${item.id}`}><Trash2 className="h-4 w-4 text-[#D9531E]" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {viewItem && (
        <Dialog open={!!viewItem} onOpenChange={(o) => !o && setViewItem(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl text-[#194324]">Details</DialogTitle><DialogDescription className="sr-only">Record details</DialogDescription></DialogHeader>
            <div className="space-y-3">
              {idField && <div><span className="text-xs text-[#B38728] font-mono uppercase">ID</span><div className="font-medium">{viewItem[idField]}</div></div>}
              {fields.map((f) => (
                <div key={f.name}>
                  <span className="text-xs text-[#B38728] font-mono uppercase">{f.label}</span>
                  <div className="text-[#314337] text-sm">{f.type === "image" || f.type === "file" ? (viewItem[f.name] ? <a className="text-[#C59B27] underline" href={fileUrl(viewItem[f.name])} target="_blank" rel="noreferrer">Open</a> : "—") : String(viewItem[f.name] ?? "—")}</div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl text-[#194324]">{editing ? "Edit" : "Add"} {title}</DialogTitle><DialogDescription className="sr-only">{editing ? "Edit" : "Create"} {title} entry</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {fields.map((f) => (
              <div key={f.name} className="space-y-2">
                <Label>{f.label}{f.required && " *"}</Label>
                {f.type === "textarea" ? (
                  <Textarea value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} rows={f.rows || 3} required={f.required} data-testid={`${endpoint}-form-${f.name}`} />
                ) : f.type === "select" ? (
                  <select value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" required={f.required} data-testid={`${endpoint}-form-${f.name}`}>
                    <option value="" disabled>Select...</option>
                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.type === "image" ? (
                  <ImageUpload value={form[f.name]} onChange={(p) => setForm({ ...form, [f.name]: p })} testid={`${endpoint}-form-${f.name}`} />
                ) : f.type === "file" ? (
                  <ImageUpload value={form[f.name]} onChange={(p) => setForm({ ...form, [f.name]: p })} testid={`${endpoint}-form-${f.name}`} accept="*" fileMode />
                ) : f.type === "number" ? (
                  <Input type="number" step="any" value={form[f.name] ?? 0} onChange={(e) => { const n = parseFloat(e.target.value); setForm({ ...form, [f.name]: Number.isNaN(n) ? 0 : n }); }} data-testid={`${endpoint}-form-${f.name}`} />
                ) : f.type === "date" ? (
                  <Input type="date" value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} required={f.required} data-testid={`${endpoint}-form-${f.name}`} />
                ) : (
                  <Input value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} required={f.required} data-testid={`${endpoint}-form-${f.name}`} />
                )}
              </div>
            ))}
            {hasPublish && (
              <div className="flex items-center gap-3 bg-[#F9F3E3] rounded-lg px-4 py-3">
                <Switch checked={!!form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid={`${endpoint}-form-publish`} />
                <span className="text-sm text-[#194324]">Publish to public site</span>
              </div>
            )}
            <DialogFooter>
              <Button type="submit" disabled={saving} className="bg-[#194324] hover:bg-[#2D623A] text-white" data-testid={`${endpoint}-form-submit`}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this entry?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={confirmDelete} className="bg-[#D9531E] hover:bg-[#b8441a]" data-testid="confirm-delete-button">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
